import type { IconName } from '@/ui';

/**
 * The application map — ONE list, four consumers.
 *
 * v1 kept these facts in four parallel structures that had to be edited
 * together and regularly weren't: `navGroups` in Sidebar.tsx, `<Route>`
 * elements in DashboardLayout.tsx, `permissionMap.ts`, and a `pageTitleKeys`
 * lookup. Adding a screen meant four edits; forgetting one gave you a page
 * that was reachable but unlabelled, or labelled but permission-less.
 *
 * Here a screen is one entry. The sidebar, the router, the permission gate and
 * the breadcrumb all read from it.
 *
 * ── Groups, paths and permissions are v1's, exactly ──
 * The six groups and their membership come from v1's `navGroups`; the
 * permission strings come from its `permissionMap.ts`. Both are reproduced
 * verbatim, because a nav item that asks for a permission the backend does not
 * issue simply disappears for real users — and a renamed group is a retraining
 * cost for staff who navigate by muscle memory.
 *
 * Note the real permission vocabulary is coarser than the screens suggest:
 * one `manage_classes_and_subjects` covers classes, subjects, allocations and
 * timetable; `manage_fees_and_invoices` covers the entire finance section.
 * Do not invent finer-grained names here — they would silently hide screens.
 */

export interface NavItem {
  id: string;
  /** URL path, without a leading slash. */
  path: string;
  /** i18n key under `nav.` — the label in the sidebar. */
  labelKey: string;
  /** i18n key under `pageTitles.` — the breadcrumb and window title. */
  titleKey: string;
  icon: IconName;
  /**
   * Any one of these permissions grants access. An empty array means the
   * screen is open to every signed-in user.
   */
  permissions: string[];
  /** Reachable by URL but hidden from the sidebar (detail and editor screens). */
  hidden?: boolean;
}

export interface NavGroup {
  id: string;
  /** i18n key under `nav.` */
  labelKey: string;
  items: NavItem[];
}

const CLASSES_AND_SUBJECTS = ['manage_classes_and_subjects'];
const GRADES_AND_EXAMS = ['manage_grades_and_exams'];
const FEES_AND_INVOICES = ['manage_fees_and_invoices'];
const SCHOOL_SETTINGS = ['manage_school_settings'];
const ORGANIZATION = ['manage_organization'];

export const NAV: NavGroup[] = [
  {
    id: 'core',
    labelKey: 'nav.core',
    items: [
      {
        id: 'dashboard',
        path: 'dashboard',
        labelKey: 'nav.dashboard',
        titleKey: 'pageTitles.dashboard',
        icon: 'dashboard',
        permissions: [],
      },
      {
        id: 'students',
        path: 'students',
        labelKey: 'nav.students',
        titleKey: 'pageTitles.students',
        icon: 'students',
        permissions: ['manage_students'],
      },
      {
        id: 'staff',
        path: 'staff',
        labelKey: 'nav.staff',
        titleKey: 'pageTitles.staff',
        icon: 'staff',
        permissions: ['manage_staff'],
      },
    ],
  },
  {
    id: 'academics',
    labelKey: 'nav.academics',
    items: [
      {
        id: 'schools',
        path: 'schools',
        labelKey: 'nav.schools',
        titleKey: 'pageTitles.schools',
        icon: 'schools',
        permissions: SCHOOL_SETTINGS,
      },
      {
        id: 'classes',
        path: 'classes',
        labelKey: 'nav.classes',
        titleKey: 'pageTitles.classes',
        icon: 'classes',
        permissions: CLASSES_AND_SUBJECTS,
      },
      {
        id: 'exams',
        path: 'exams',
        labelKey: 'nav.exams',
        titleKey: 'pageTitles.exams',
        icon: 'exams',
        permissions: GRADES_AND_EXAMS,
      },
      {
        id: 'examSchedule',
        path: 'exams/schedule',
        labelKey: 'nav.exams',
        titleKey: 'pageTitles.examSchedule',
        icon: 'exams',
        permissions: GRADES_AND_EXAMS,
        hidden: true,
      },
      {
        id: 'marks',
        path: 'marks',
        labelKey: 'nav.marksEntry',
        titleKey: 'pageTitles.marks',
        icon: 'marks',
        permissions: GRADES_AND_EXAMS,
      },
      {
        id: 'results',
        path: 'results',
        labelKey: 'nav.viewResults',
        titleKey: 'pageTitles.results',
        icon: 'results',
        permissions: GRADES_AND_EXAMS,
      },
      {
        id: 'homework',
        path: 'homework',
        labelKey: 'nav.homework',
        titleKey: 'pageTitles.homework',
        icon: 'homework',
        permissions: ['manage_assignments'],
      },
      {
        id: 'homeworkEval',
        path: 'homework/evaluate',
        labelKey: 'nav.homework',
        titleKey: 'pageTitles.homeworkEval',
        icon: 'homework',
        permissions: ['manage_assignments'],
        hidden: true,
      },
    ],
  },
  {
    id: 'operations',
    labelKey: 'nav.operations',
    items: [
      {
        id: 'attendance',
        path: 'attendance',
        labelKey: 'nav.attendance',
        titleKey: 'pageTitles.attendance',
        icon: 'attendance',
        permissions: ['manage_attendance'],
      },
      {
        id: 'timetable',
        path: 'timetable',
        labelKey: 'nav.timetable',
        titleKey: 'pageTitles.timetable',
        icon: 'timetable',
        permissions: CLASSES_AND_SUBJECTS,
      },
      {
        id: 'gatePass',
        path: 'gate-pass',
        labelKey: 'nav.gatePass',
        titleKey: 'pageTitles.gatePass',
        icon: 'gatePass',
        permissions: ['manage_attendance'],
      },
      {
        id: 'noticeboard',
        path: 'noticeboard',
        labelKey: 'nav.noticeboard',
        titleKey: 'pageTitles.noticeboard',
        icon: 'notice',
        permissions: ['manage_notices'],
      },
      {
        id: 'holidays',
        path: 'holidays',
        labelKey: 'nav.holidays',
        titleKey: 'pageTitles.holidays',
        icon: 'holidays',
        permissions: SCHOOL_SETTINGS,
      },
      {
        id: 'tasks',
        path: 'tasks',
        labelKey: 'nav.tasks',
        titleKey: 'pageTitles.tasks',
        icon: 'tasks',
        permissions: [],
      },
    ],
  },
  {
    id: 'transport',
    labelKey: 'nav.transport',
    items: [
      {
        id: 'fleet',
        path: 'fleet',
        labelKey: 'nav.fleet',
        titleKey: 'pageTitles.fleet',
        icon: 'transport',
        permissions: ORGANIZATION,
      },
      {
        id: 'routeBuilder',
        path: 'route-builder',
        labelKey: 'nav.routes',
        titleKey: 'pageTitles.routeBuilder',
        icon: 'routes',
        permissions: ORGANIZATION,
      },
      {
        id: 'passengerAlloc',
        path: 'passenger-allocations',
        labelKey: 'nav.passengers',
        titleKey: 'pageTitles.passengerAlloc',
        icon: 'students',
        permissions: ORGANIZATION,
      },
    ],
  },
  {
    id: 'financials',
    labelKey: 'nav.financials',
    items: [
      {
        id: 'feeStructures',
        path: 'fee-structures',
        labelKey: 'nav.feeStructures',
        titleKey: 'pageTitles.feeStructures',
        icon: 'fees',
        permissions: FEES_AND_INVOICES,
      },
      {
        id: 'transactions',
        path: 'transactions',
        labelKey: 'nav.transactions',
        titleKey: 'pageTitles.transactions',
        icon: 'transactions',
        permissions: FEES_AND_INVOICES,
      },
      {
        id: 'collection',
        path: 'collection',
        labelKey: 'nav.collectionDesk',
        titleKey: 'pageTitles.collection',
        icon: 'collection',
        permissions: FEES_AND_INVOICES,
      },
      /* Invoices is hidden from the sidebar, not deleted: what the office asks
         of this section is "what has been paid", and the transaction ledger
         answers it directly where the invoice register answers it a step
         removed. The screen still works and `/invoices` still resolves, so
         bookmarks and the drawer's `?invoice=` deep links keep working — and
         restoring it to the sidebar is deleting one line.

         Nothing in the app navigates here: no Link, no navigate(), checked
         before hiding it. The row menu inside the ledger only opens its own
         drawer. So this removes a way in, not a way through. */
      {
        id: 'invoices',
        path: 'invoices',
        labelKey: 'nav.invoices',
        titleKey: 'pageTitles.invoices',
        icon: 'invoices',
        permissions: FEES_AND_INVOICES,
        hidden: true,
      },
      {
        id: 'expenses',
        path: 'expenses',
        labelKey: 'nav.expenses',
        titleKey: 'pageTitles.expenses',
        icon: 'expenses',
        // Deliberately different from expenseCategories below — v1 let a clerk
        // record an expense without being able to edit the category list.
        permissions: ['record_expenses'],
      },
      {
        id: 'studentDiscounts',
        path: 'student-discounts',
        labelKey: 'nav.studentDiscounts',
        titleKey: 'pageTitles.studentDiscounts',
        icon: 'fees',
        permissions: FEES_AND_INVOICES,
      },
      {
        id: 'expenseCategories',
        path: 'expense-categories',
        labelKey: 'nav.expenseCategories',
        titleKey: 'pageTitles.expenseCategories',
        icon: 'expenses',
        permissions: ['manage_expenses'],
      },
      {
        id: 'reports',
        path: 'reports',
        labelKey: 'nav.reports',
        titleKey: 'pageTitles.reports',
        icon: 'reports',
        permissions: FEES_AND_INVOICES,
      },
    ],
  },
  {
    id: 'admin',
    labelKey: 'nav.admin',
    items: [
      /* First-run setup. Listed first in this group because it is the answer to
         "the app is empty, where do I start" — the four records it creates are
         what every other screen is scoped by.

         It asks for `manage_school_settings` and nothing else, even though its
         last step creates classes. Permissions here are *any one grants*, so
         adding `manage_classes_and_subjects` would let a user who cannot create
         a school into a wizard whose first two steps they would fail — widening
         access rather than describing it. The class step is the one the sessions
         and schools administrator also has in practice. */
      {
        id: 'setup',
        path: 'setup',
        labelKey: 'nav.setup',
        titleKey: 'pageTitles.setup',
        icon: 'circleCheck',
        permissions: SCHOOL_SETTINGS,
      },
      {
        id: 'organization',
        path: 'organization',
        labelKey: 'nav.organization',
        titleKey: 'pageTitles.organization',
        icon: 'settings',
        permissions: ORGANIZATION,
      },
      {
        id: 'departments',
        path: 'departments',
        labelKey: 'nav.departments',
        titleKey: 'pageTitles.departments',
        icon: 'schools',
        permissions: ORGANIZATION,
      },
      {
        id: 'designations',
        path: 'designations',
        labelKey: 'nav.designations',
        titleKey: 'pageTitles.designations',
        icon: 'staff',
        permissions: ORGANIZATION,
      },
      {
        id: 'subjects',
        path: 'subjects',
        labelKey: 'nav.subjects',
        titleKey: 'pageTitles.subjects',
        icon: 'subjects',
        permissions: CLASSES_AND_SUBJECTS,
      },
      {
        id: 'schoolSubjects',
        path: 'school-subjects',
        labelKey: 'nav.schoolSubjects',
        titleKey: 'pageTitles.schoolSubjects',
        icon: 'subjects',
        permissions: CLASSES_AND_SUBJECTS,
      },
      {
        id: 'allocations',
        path: 'allocations',
        labelKey: 'nav.allocations',
        titleKey: 'pageTitles.allocations',
        icon: 'subjects',
        permissions: CLASSES_AND_SUBJECTS,
      },
      {
        id: 'sessions',
        path: 'sessions',
        labelKey: 'nav.sessions',
        titleKey: 'pageTitles.sessions',
        icon: 'holidays',
        permissions: SCHOOL_SETTINGS,
      },
    ],
  },
];

/** Flat index of every screen, hidden ones included. */
export const NAV_ITEMS: NavItem[] = NAV.flatMap((g) => g.items);

/**
 * The nav entry owning a pathname.
 *
 * Matches the longest path first so `/homework/evaluate` resolves to the
 * evaluation screen rather than to `/homework`.
 */
export function findNavItem(pathname: string): NavItem | undefined {
  const path = pathname.replace(/^\/+/, '');
  return [...NAV_ITEMS]
    .sort((a, b) => b.path.length - a.path.length)
    .find((item) => path === item.path || path.startsWith(`${item.path}/`));
}
